import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  computed,
  signal,
  ChangeDetectionStrategy,
  effect,
} from '@angular/core';
import {
  FilterCriteria,
  FilterDetail,
} from '../../../models/api/reports/filter-detail-response';
import { BaseComponent } from '../../base/base.component';
import { DetailItemComponent } from '../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../core/panels/detail-panel/detail-panel.component';
import { FilterGroupVisibility } from '../../../models/api/reports/filter-security';

@Component({
  selector: 'app-filter-view',
  imports: [DetailPanelComponent, DetailItemComponent, CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (dataSourceValue()) {
      <div class="tw-overflow-y-auto tw-max-h-[400px] tw-px-4">
        <!-- Filter Info Section -->
        <app-detail-panel [dataSource]="filterSummary()">
          <app-detail-item label="Filter Name" name="filterName" />
          @if (filterSummary().description) {
            <app-detail-item label="Description" name="description" />
          }
        </app-detail-panel>

        <!-- Criteria Sections -->
        @if (!visibilityLoading()) {
        @for (section of groupedCriteria(); track section.sectionName) {
          <div>
            <hr class="tw-my-4" />
            <div class="tw-flex tw-justify-between tw-items-center tw-pb-1">
              <!-- Section Header -->
              <h3
                class="tw-text-xl tw-font-bold tw-text-[var(--sed-engaged-black-color)]"
              >
                {{ section.sectionName }}
              </h3>
            </div>

            @if (section.hasSubsections) {
              <!-- Grouped by subsections -->
              @for (
                subsection of section.subsections;
                track subsection.subsectionName
              ) {
                @if (subsection.subsectionName) {
                  <div
                    class="tw-flex tw-justify-between tw-items-center tw-pl-4 tw-pt-2"
                  >
                    <h4
                      class="tw-text-lg tw-font-bold tw-text-[var(--sed-engaged-black-color)]"
                    >
                      {{ subsection.subsectionName }}
                    </h4>
                  </div>
                }

                <app-detail-panel
                  [dataSource]="subsection.summary"
                  [classes]="subsection.subsectionName ? 'tw-pl-8' : 'tw-pl-4'"
                >
                  @for (
                    criterion of subsection.criteria;
                    track getCriteriaKey(criterion)
                  ) {
                    <app-detail-item
                      [label]="getLabel(criterion)"
                      [name]="getCriteriaKey(criterion)"
                    />
                  }
                </app-detail-panel>
              }
            } @else {
              <!-- No subsections, show criteria directly -->
              <app-detail-panel
                [dataSource]="section.summary"
                classes="tw-pl-4"
              >
                @for (
                  criterion of section.criteria;
                  track getCriteriaKey(criterion)
                ) {
                  <app-detail-item
                    [label]="getLabel(criterion)"
                    [name]="getCriteriaKey(criterion)"
                  />
                }
              </app-detail-panel>
            }
          </div>
        }
        }
      </div>
    }
  `,
  styles: ``,
})
export class FilterViewComponent extends BaseComponent {
  private _dataSourceSignal = signal<FilterDetail | null>(null);
  filterGroupVisibility = signal<FilterGroupVisibility | null>(null);
  visibilityLoading = signal<boolean>(false);

  protected readonly dataSourceValue = this._dataSourceSignal.asReadonly();

  @Input()
  set dataSource(value: FilterDetail | null) {
    this._dataSourceSignal.set(value);
  }

  constructor() {
    super();

    effect(async () => {
      const data = this._dataSourceSignal();
      if (data?.contractNumber) {
        this.visibilityLoading.set(true);
        const groupVisibility =
          await this.api.filtersCriteria.getFilterGroupVisibility(
            data.contractNumber,
          );
        this.filterGroupVisibility.set(groupVisibility);
        this.visibilityLoading.set(false);
      } else {
        this.filterGroupVisibility.set(null);
        this.visibilityLoading.set(false);
      }
    });
  }

  filterSummary = computed(() => {
    const data = this._dataSourceSignal();
    if (!data) {
      return {};
    }

    return {
      filterName: data.filterName,
      description: data.description,
    };
  });

  groupedCriteria = computed(() => {
    const data = this._dataSourceSignal();
    if (!data || !data.criteria) {
      return [];
    }

    const grouped = new Map<string, FilterCriteria[]>();
    const visibility = this.filterGroupVisibility();
    for (const criterion of data.criteria) {
      if (!criterion.filterSection) {
        continue;
      }

      const sectionName = `${criterion.filterSection}`;
      if (this.showFilterSection(visibility, sectionName)) {
        if (!grouped.has(sectionName)) {
          grouped.set(sectionName, []);
        }
        grouped.get(sectionName)!.push(criterion);
      }
    }

    return Array.from(grouped.entries()).map(([sectionName, criteria]) => {
      const hasSubsections = criteria.some((c) => c.filterSubsection);

      if (hasSubsections) {
        const subsectionMap = new Map<string, FilterCriteria[]>();

        for (const criterion of criteria) {
          const subsectionName = criterion.filterSubsection || '';
          if (!subsectionMap.has(subsectionName)) {
            subsectionMap.set(subsectionName, []);
          }
          subsectionMap.get(subsectionName)!.push(criterion);
        }

        const subsections = Array.from(subsectionMap.entries()).map(
          ([subsectionName, subsectionCriteria]) => {
            const summary: Record<string, string> = {};
            for (const crit of subsectionCriteria) {
              const key = this.getCriteriaKey(crit);
              summary[key] = crit.formattedValue;
            }

            return {
              subsectionName,
              criteria: subsectionCriteria,
              summary,
            };
          },
        );

        subsections.sort((a, b) =>
          a.subsectionName?.localeCompare(b.subsectionName),
        );

        const sectionSummary: Record<string, string> = {};
        for (const crit of criteria) {
          const key = this.getCriteriaKey(crit);
          sectionSummary[key] = crit.formattedValue;
        }

        return {
          sectionName,
          criteria,
          hasSubsections: true,
          subsections,
          summary: sectionSummary,
        };
      }

      const sectionSummary: Record<string, string> = {};
      for (const crit of criteria) {
        const key = this.getCriteriaKey(crit);
        sectionSummary[key] = crit.formattedValue;
      }

      return {
        sectionName,
        criteria,
        hasSubsections: false,
        subsections: [] as {
          subsectionName: string;
          criteria: FilterCriteria[];
          summary: Record<string, string>;
        }[],
        summary: sectionSummary,
      };
    });
  });

  getLabel(criteria: FilterCriteria): string {
    if (criteria.formattedOperator) {
      return `${criteria.displayName} ${criteria.formattedOperator}`;
    }
    return criteria.displayName;
  }

  getCriteriaKey(criteria: FilterCriteria): string {
    return `${criteria.tableAlias}_${criteria.column}_${criteria.operator}`;
  }

  private showFilterSection(
    visibility: FilterGroupVisibility | null,
    section: string,
  ): boolean {
    if (!visibility) {
      return true;
    }

    switch (section) {
      case 'Financial':
        return visibility.showFinancial ?? true;
      case 'Flag':
        return visibility.showClaimFlags ?? true;
      case 'Work Status':
        return visibility.showWorkStatus ?? true;
      case 'Client Field':
        return visibility.showClientField ?? true;
      case 'Loss':
        return visibility.showLossCodes ?? true;
      case 'Examiner':
        return visibility.showExaminer ?? true;
      default:
        return true;
    }
  }
}
